/**
 * A Snappy identity call (`/v2/snappy/*` on MoonHub.API, the launch admission
 * and voucher check on factions) failed.
 *
 * Same shape and `name` as InOut.Mobile's `SnappyApiError`, so the vendored
 * presenter describes both: `status` is 0 when the request never got an answer
 * (`code` 'NETWORK'), `code` is the server's error code (`RATE_LIMITED`,
 * `voucherInvalid`, ...) or 'BAD_RESPONSE' for an answer that does not match
 * the expected shape. The server's own text is kept in `detail` for logs only;
 * it is never shown to the user.
 */
export class SnappyApiError extends Error {
    readonly status: number;
    readonly code: string | null;
    readonly detail: string | null;

    constructor(
        status: number,
        code: string | null,
        opts: { detail?: string | null; cause?: unknown } = {},
    ) {
        super(
            `Snappy API request failed (${status}${code ? ` ${code}` : ''})`,
            {
                cause: opts.cause,
            },
        );
        this.name = 'SnappyApiError';
        this.status = status;
        this.code = code;
        this.detail = opts.detail ?? null;
    }
}

/**
 * The social session behind a Snappy call is gone (HTTP 401): the user has to
 * sign in again before anything account-bound can be read or written.
 */
export class SnappySessionExpiredError extends SnappyApiError {
    constructor(
        code: string | null = 'NOT_AUTHENTICATED',
        opts: { detail?: string | null; cause?: unknown } = {},
    ) {
        super(401, code, opts);
        this.name = 'SnappySessionExpiredError';
    }
}

/** Errors cross structured-clone boundaries and lose their prototypes. */
function hasName(error: unknown, ...names: string[]): boolean {
    return (
        typeof error === 'object' &&
        error !== null &&
        names.includes((error as { name?: unknown }).name as string)
    );
}

export function isSnappySessionExpiredError(
    error: unknown,
): error is SnappySessionExpiredError {
    return (
        error instanceof SnappySessionExpiredError ||
        hasName(error, 'SnappySessionExpiredError')
    );
}

export function isSnappyApiError(error: unknown): error is SnappyApiError {
    return (
        error instanceof SnappyApiError ||
        hasName(error, 'SnappyApiError', 'SnappySessionExpiredError')
    );
}
